import Vue from 'vue'
import Router from 'vue-router'
import UserService from '../services/user.service'
import { store } from '../store'
import appState from '../store/app'
import { sync } from 'vuex-router-sync'
import Alert from '../libs/alert/'
import { isFunction } from '../libs/utils'
const SignIn = () => import(/* webpackChunkName: "signin" */ '@/pages/SignIn')
const Layout = () => import(/* webpackChunkName: "main" */ '@/Layout')
// const Dashboard = () => import(/* webpackChunkName: "main" */ '@/pages/Dashboard')
const OnBoarding = () => import(/* webpackChunkName: "main" */ '@/pages/OnBoarding/OnBoarding')
const SaleMonitoring = () => import(/* webpackChunkName: "main" */ '@/pages/SaleMonitoring/SaleMonitoring')
const ScriptMonitoring = () => import(/* webpackChunkName: "main" */ '@/pages/ScriptMonitoring/ScriptMonitoring')
const BotMonitoring = () => import(/* webpackChunkName: "main" */ '@/pages/BotMonitoring/BotMonitoring')
const BotManagement = () => import(/* webpackChunkName: "main" */ '@/pages/BotManagement/BotManagement')
const Balances = () => import(/* webpackChunkName: "main" */ '@/pages/Balances/Balances')
const Encryption = () => import(/* webpackChunkName: "main" */ '@/pages/Encryption')
const Spreadsheet = () => import(/* webpackChunkName: "main" */ '@/pages/Spreadsheet')
const Collection = () => import(/* webpackChunkName: "main" */ '@/pages/Collection')
const FundManagement = () => import(/* webpackChunkName: "main" */ '@/pages/FundManagement/FundManagement')
const VirtualConfig = () => import(/* webpackChunkName: "main" */ '@/pages/VirtualConfig/VirtualConfig')
const HistoricalSales = () => import(/* webpackChunkName: "main" */ '@/pages/HistoricalSales/HistoricalSales')
const HistoricalTrades = () => import(/* webpackChunkName: "historical-trades" */'@/pages/HistoricalTrades/HistoricalTrades')
const ReportConfiguration = () => import('@/pages/ReportConfiguration/ReportConfiguration')
const Buyback = () => import('@/pages/Buyback/Buyback')
const DownloadReport = () => import('@/pages/DownloadReport.vue')

Vue.use(Router)

const delayFirstTime = (() => {
	const MIN_DELAY_TIME = 0
	let isFirstTime = false
	return (time, next) => {
		if (!isFirstTime) {
			isFirstTime = true
			const diff = Date.now() - time
			const wait = MIN_DELAY_TIME - diff
			setTimeout(next, wait < 0 ? 0 : wait)
		} else {
			next()
		}
	}
})()
const router = new Router({
	mode: 'history',
	routes: [
		{
			path: '/signin',
			name: 'signin',
			component: SignIn,
			meta: {
				loader: true
			},
			async beforeEnter(to, from, next) {
				const time = Date.now()
				if (await UserService.isLoggedIn()) {
					if (await UserService.checkAccessToken()) {
						delayFirstTime(time, () => next('/'))
					} else {
						await UserService.logout()
						delayFirstTime(time, () => next())
					}
				} else {
					delayFirstTime(time, () => next())
				}
			}
		},
		{
			path: '/',
			component: Layout,
			meta: {
				loader: true
			},
			async beforeEnter(to, from, next) {
				const time = Date.now()
				// Check if store is old version, then logout automatically.
				const idOldVersion = store.getters['user/isOldVersion']
				await store.dispatch('user/setVersionToLatest')
				if (idOldVersion) {
					await UserService.logout()
					delayFirstTime(time, () => next({ name: 'signin', query: { redirect: to.fullPath } }))
					return
				}
				if (!await UserService.isLoggedIn()) {
					delayFirstTime(time, () => next({ name: 'signin', query: { redirect: to.fullPath } }))
				} else {
					if (await UserService.checkAccessToken()) {
						delayFirstTime(time, () => next())
					} else {
						Alert.error({
							emergency: true,
							title: 'Attention',
							message: 'You have disconnected or your <b>`Access Token`</b> is not working'
						})
						await UserService.logout()
						delayFirstTime(time, () => next({ name: 'signin', query: { redirect: to.fullPath } }))
					}
				}
			},
			children: [
				{
					path: '',
					redirect: $route => {
						const role = store.getters['user/role']
						if (role) {
							switch (role.name) {
								case UserService.ROLES['ADMIN'].name: return { name: 'botMonitoring' }
								case UserService.ROLES['SUPERVISOR'].name: return { name: 'botMonitoring' }
								case UserService.ROLES['DEVELOPER'].name: return { name: 'botMonitoring' }
								case UserService.ROLES['GUEST'].name:
								default: return { name: 'botMonitoring' }
							}
						}
						return { name: 'signin' }
					},
					meta: {
						show: false
					}
				}, {
					path: 'sale-monitoring/:date?',
					name: 'saleMonitoring',
					component: SaleMonitoring,
					meta: {
						permission: 'sale-monitoring',
						exact: false,
						icon: 'mdi-currency-usd',
						title: 'Sale Monitoring'
					},
					beforeEnter(to, from, next) {
						const { date } = to.query
						if (!date) {
							router.push({ name: 'saleMonitoring', query: { date: new Date().toISOString().split('T')[0] } })
						} else {
							next()
						}
					},
					props: ($route) => {
						const { date } = $route.query
						let dateObj = Date.parse(date)
						if (isNaN(dateObj)) {
							dateObj = new Date()
						} else {
							dateObj = new Date(date)
						}
						return {
							date: dateObj.toISOString().split('T')[0]
						}
					}
				}, {
					path: 'script-monitoring',
					name: 'scriptMonitoring',
					component: ScriptMonitoring,
					meta: {
						permission: 'script-monitoring',
						icon: 'mdi-monitor',
						title: 'Script Monitoring'
					}
				}, 
				{
					path: 'account-config',
					name: 'accountConfig',
					component: OnBoarding,
					meta: {
						permission: 'account-config',
						exact: false,
						icon: 'mdi-tag-faces',
						title: 'Account Configs'
					}
				},
				{
					path: 'bot-management',
					name: 'botManagement',
					component: BotManagement,
					meta: {
						permission: 'bot-management',
						exact: false,
						icon: 'mdi-power',
						title: 'Bot Management'
					}
				}, {
					path: 'bot-monitoring/:account?',
					name: 'botMonitoring',
					component: BotMonitoring,
					meta: {
						permission: 'bot-monitoring',
						exact: false,
						icon: 'mdi-robot',
						title: 'Bot Monitoring'
					},
					props: ($route) => {
						const { account } = $route.query
						return {
							account
						}
					}
				}, {
					path: 'virtual-config',
					name: 'virtualConfig',
					component: VirtualConfig,
					meta: {
						permission: 'virtual-config',
						icon: 'fa-magic',
						title: 'Virtual Config'
					}
				}, {
					path: 'balances',
					name: 'balances',
					component: Balances,
					meta: {
						permission: 'balances',
						icon: 'fas fa-hand-holding-usd',
						title: 'Balances'
					}
				},
				{
					path: 'encryption',
					name: 'encryption',
					component: Encryption,
					meta: {
						permission: 'encryption',
						icon: 'mdi-key',
						title: 'Encryption'
					}
				},
				{
					path: 'spreadsheet',
					name: 'spreadsheet',
					component: Spreadsheet,
					meta: {
						permission: 'spreadsheet',
						icon: 'mdi-file-document-box',
						title: 'Spreadsheet'
					}
				},
				{
					path: 'downloadReport',
					name: 'downloadReport',
					component: DownloadReport,
					meta: {
						permission: 'downloadReport',
						icon: 'mdi-download',
						title: 'Download Report'
					}
				},
				{
					path: 'fund-management',
					name: 'fund-management',
					component: FundManagement,
					meta: {
						permission: 'fund-management',
						icon: 'mdi-tune',
						title: 'Fund Management'
					}
				},
				{
					path: 'historical-sales',
					name: 'historical-sales',
					component: HistoricalSales,
					meta: {
						permission: 'historical-sales',
						icon: 'fas fa-chart-line',
						title: 'Historical Sales'
					}
				},
				{
					path: 'historical-trades',
					name: 'historical-trades',
					component: HistoricalTrades,
					meta: {
						permission: 'historical-trades',
						icon: 'fas fa-exchange-alt',
						title: 'Historical Trades'
					}
				},
				{
					path: 'report-configuration',
					name: 'report-configuration',
					component: ReportConfiguration,
					meta: {
						permission: 'report-configuration',
						icon: 'build',
						title: 'Bot Report Configuration'
					}
				},
				{
					path: 'buyback',
					name: 'buyback',
					component: Buyback,
					meta: {
						permission: 'buyback',
						icon: 'fa-sharp fa-regular fa-repeat',
						title: 'Buyback'
					}
				},
				{
					path: 'collection/:collection',
					name: 'collection',
					component: Collection,
					meta: {
						permission: $route => [$route.name, $route.params['collection']],
						show: false
					}
				},
				{
					path: 'arbitrage/:collection',
					name: 'arbitrage-collection',
					component: Collection,
					meta: {
						permission: $route => [$route.name, $route.params['collection']],
						show: false,
						database: 'ARBITRAGE',
					}
				},
			]
		},
		{
			path: '*',
			redirect: '/'
		}
	]
})
router.beforeEach(async (to, from, next) => {
	if (to.matched.some(m => m.meta.loader)) {
		appState.loading = true
	}
	if (to.meta.permission) {
		const permissionParams = isFunction(to.meta.permission) ? to.meta.permission(to) : [to.meta.permission]
		const authorized = await UserService.hasPermission(...permissionParams)
		// console.log('permission', permissionParams, authorized)
		if (authorized) {
			return next()
		} else {
			return next('/')
		}
	}
	next()
})
router.afterEach((to, from) => {
	appState.loading = false
})

sync(store, router)

export default router
